/**
 * The `structures/{id}` document, as the data layer admits it.
 *
 * LENIENT ON PURPOSE. A structure document carries forty-odd fields from the
 * pipeline (`prealignment-*`, `scanProvenance`, `alignmentMetadata`, …) and
 * every element inside its arrays carries twenty more; a strict schema would
 * refuse real documents on a field nobody reads. What this schema is strict
 * about is the shape every reader depends on: ids, and the geometry triples.
 * Everything else passes through. Fields tighten as repositories start to
 * read them, never ahead of that.
 *
 * Measured on the emulator corpus (58 structures, 2026-10-21): every wall and
 * every opening has `id`, `center`, `dimensions` and `rotation`; every
 * opening has `parentWallIds`; no array holds a `null` — but
 * `updateStructureWallGeometry` preserves one on purpose (index stability),
 * so `walls` admits them.
 *
 * Parse cost is on the read path (every snapshot), which is why the arrays
 * are not deep-validated beyond those fields; see the plan's performance
 * section. `safeParse`, never `parse`, at the boundary: a document that
 * fails is reported, not blanked.
 *
 * Every object shape is `.passthrough()`, the geometry triples included: a
 * plain `z.object` strips the keys it does not name, and the parsed copy is
 * the one the session holds and writes from. The room schema lost every
 * object's extra `dimensions` keys to exactly that (#2886);
 * `schemaPassthrough.ci.test.ts` refuses a shape that strips.
 */
import { z } from 'zod';

export const Vec3Schema = z
  .object({
    x: z.number(),
    y: z.number(),
    z: z.number(),
  })
  .passthrough();

/** A structure-level wall. */
export const WallSchema = z
  .object({
    id: z.string().min(1),
    center: Vec3Schema,
    dimensions: Vec3Schema,
    rotation: Vec3Schema,
    story: z.number().nullable().optional(),
    polygonCorners: z.array(z.unknown()).nullable().optional(),
    leftMiter: z.number().nullable().optional(),
    rightMiter: z.number().nullable().optional(),
  })
  .passthrough();

/**
 * A CANONICAL opening (door / window / opening) on the structure document —
 * the one with geometry. The slim copies inside a room document are a
 * different shape (`{ id, structureOpeningId, parentWallIds }`) and belong to
 * the room schema.
 */
export const OpeningSchema = z
  .object({
    id: z.string().min(1),
    center: Vec3Schema,
    dimensions: Vec3Schema,
    rotation: Vec3Schema.nullable().optional(),
    parentWallIds: z.array(z.string().nullable()).nullable().optional(),
    story: z.number().nullable().optional(),
  })
  .passthrough();

/**
 * An element array that older documents may hold as `null` (a field written
 * by a pipeline that had nothing to put there), read as empty. `.default([])`
 * covers only a MISSING field; a written null is a value and must be
 * replaced before the array check or the whole document fails the schema —
 * and then logs on every snapshot.
 */
const nullableArray = <T extends z.ZodTypeAny>(element: T) =>
  z.preprocess(
    (value) => (value == null ? [] : value),
    z.array(element)
  ) as unknown as z.ZodDefault<z.ZodArray<T>>;

/** The four arrays an operation set may address, by Firestore field name. */
export const STRUCTURE_ARRAY_KEYS = [
  'walls',
  'doors',
  'windows',
  'openings',
] as const;
export type StructureArrayKey = (typeof STRUCTURE_ARRAY_KEYS)[number];

export const StructureDocSchema = z
  .object({
    id: z.string().min(1),
    // Null is a value a real document may carry on any of these — a scan
    // with no name yet, a structure outside a project, a cleared exterior,
    // a deleted slot in an opening array — and a schema that refused it
    // logged on every snapshot of that document for the life of the page.
    name: z.string().nullable().optional(),
    projectId: z.string().nullable().optional(),
    walls: nullableArray(WallSchema.nullable()),
    doors: nullableArray(OpeningSchema.nullable()),
    windows: nullableArray(OpeningSchema.nullable()),
    openings: nullableArray(OpeningSchema.nullable()),
    exterior: z.record(z.unknown()).nullable().optional(),
    depthFinalization: z.record(z.unknown()).nullable().optional(),
  })
  .passthrough();
